import { useState } from 'react';
import { Table, Button, Space, Switch, Tag, Tooltip, App, theme, type TableColumnsType } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HolderOutlined, BankOutlined, CheckCircleOutlined, StopOutlined, ProfileOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useInvestors, useUpdateInvestor, useDeleteInvestor } from '@/hooks/useInvestors';
import { getInvestorsStats } from '@/api/investorsStats';
import { AddInvestorModal } from './AddInvestorModal';
import { EditInvestorModal } from './EditInvestorModal';
import { EmptyState } from '@/components/EmptyState';
import { PageHero, HeroChip } from '@/components/PageHero';
import { KpiTile } from '@/components/KpiTile';
import { SectionCard } from '@/components/SectionCard';
import type { Investor } from '@/api/investors';

export function InvestorsListPage() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const { token } = theme.useToken();
  const q = useInvestors(true); // include inactive in admin view
  const update = useUpdateInvestor();
  const del = useDeleteInvestor();
  const statsQ = useQuery({ queryKey: ['investors-stats'], queryFn: getInvestorsStats });
  const stats = statsQ.data;
  const countByName = new Map((stats?.by_investor ?? []).map((b) => [b.name, b.count]));
  const invalidateStats = () => qc.invalidateQueries({ queryKey: ['investors-stats'] });

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Investor | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);

  async function toggleActive(inv: Investor, next: boolean) {
    try {
      await update.mutateAsync({ id: inv.id, input: { active: next } });
      invalidateStats();
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  async function moveTo(src: Investor, targetIdx: number) {
    const sorted = [...(q.data ?? [])].sort((a, b) => a.display_order - b.display_order);
    const filtered = sorted.filter((i) => i.id !== src.id);
    filtered.splice(targetIdx, 0, src);
    try {
      for (let i = 0; i < filtered.length; i++) {
        if (filtered[i].display_order !== i + 1) {
          await update.mutateAsync({ id: filtered[i].id, input: { display_order: i + 1 } });
        }
      }
      invalidateStats();
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  function confirmDelete(inv: Investor) {
    const count = countByName.get(inv.name) ?? 0;
    modal.confirm({
      title: `Delete investor "${inv.name}"?`,
      content:
        count > 0
          ? `${count} topic(s) still reference this investor. Their __structured__.investor will keep the string but no dropdown option will exist. Delete anyway?`
          : 'No topics currently reference this investor.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await del.mutateAsync(inv.id);
          message.success('Deleted');
          invalidateStats();
        } catch (e) {
          message.error((e as Error).message);
        }
      },
    });
  }

  const columns: TableColumnsType<Investor> = [
    { title: '', key: 'drag', width: 30, render: () => <HolderOutlined style={{ cursor: 'grab', color: '#999' }} /> },
    { title: 'Name', dataIndex: 'name', render: (v) => <strong>{v}</strong> },
    { title: 'Order', dataIndex: 'display_order', width: 80 },
    { title: 'Active', dataIndex: 'active', width: 90, render: (v: boolean, inv) => <Switch checked={v} onChange={(n) => toggleActive(inv, n)} /> },
    {
      title: 'Topics', key: 'topic_count', width: 90,
      render: (_, inv) => {
        const c = countByName.get(inv.name) ?? 0;
        return c > 0 ? <Tag color="blue">{c}</Tag> : <Tag>0</Tag>;
      },
    },
    {
      title: 'Actions', width: 120,
      render: (_, inv) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => setEditing(inv)} /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => confirmDelete(inv)} /></Tooltip>
        </Space>
      ),
    },
  ];

  const maxCount = Math.max(1, ...(stats?.by_investor ?? []).map((b) => b.count));
  const Bar = ({ label, count }: { label: string; count: number }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: token.colorTextSecondary }}>{count}</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: token.colorFillSecondary }}>
        <div style={{ height: 8, borderRadius: 4, width: `${Math.round((count / maxCount) * 100)}%`, background: '#7c3aed' }} />
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      <PageHero
        icon={<BankOutlined />}
        title="Investors"
        subtitle="Investor list and topic coverage"
        from="#7c3aed" to="#a855f7"
        chips={[
          <HeroChip key="total">Total {stats?.total ?? 0}</HeroChip>,
          <HeroChip key="active">Active {stats?.active ?? 0}</HeroChip>,
        ]}
        actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add Investor</Button>}
      />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <KpiTile tone="sky" icon={<BankOutlined />} label="Total" value={stats?.total ?? 0} />
        <KpiTile tone="green" icon={<CheckCircleOutlined />} label="Active" value={stats?.active ?? 0} />
        <KpiTile tone="slate" icon={<StopOutlined />} label="Inactive" value={stats?.inactive ?? 0} />
        <KpiTile tone="amber" icon={<ProfileOutlined />} label="Tagged topics" value={stats?.tagged_topics ?? 0} />
      </div>

      <SectionCard tone="violet" icon={<ProfileOutlined />} title="Topics per investor" desc="Topics tagged with each investor">
        {(stats?.by_investor ?? []).map((b) => <Bar key={b.name} label={b.name} count={b.count} />)}
      </SectionCard>

      <SectionCard tone="violet" icon={<BankOutlined />} title="All investors" desc="Reorder, toggle, edit">
        <Table
          rowKey="id"
          loading={q.isLoading}
          columns={columns}
          dataSource={[...(q.data ?? [])].sort((a, b) => a.display_order - b.display_order)}
          pagination={false}
          locale={{
            emptyText: (
              <EmptyState title="No investors yet" description="Add the first investor so topics can be tagged with one." ctaLabel="Add Investor" onCta={() => setAddOpen(true)} />
            ),
          }}
          onRow={(record, index) => ({
            draggable: true,
            onDragStart: () => setDragKey(record.id),
            onDragOver: (e) => e.preventDefault(),
            onDrop: () => {
              if (dragKey && dragKey !== record.id && index !== undefined) {
                const src = (q.data ?? []).find((i) => i.id === dragKey);
                if (src) moveTo(src, index);
              }
              setDragKey(null);
            },
          })}
        />
      </SectionCard>

      <AddInvestorModal open={addOpen} onClose={() => { setAddOpen(false); invalidateStats(); }} />
      <EditInvestorModal open={!!editing} investor={editing} onClose={() => { setEditing(null); invalidateStats(); }} />
    </div>
  );
}
